import { useId, useState } from 'react';
import { formatNumber } from '@/lib/format';

/** Categorical slots 1–2 (validated: CVD ΔE 24.7, ≥3:1 on white). Sequential/single-series uses slot 1. */
export const SERIES = ['#2a78d6', '#eb6834'] as const;

export interface LineSeries {
  label: string;
  values: number[];
}

/** Axis max that splits into 4 round steps (0, 10, 20, 30, 40 — never 7.5 or 22.5). */
function niceMax(v: number): number {
  const raw = Math.max(1, v) / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  // 2.5 only reads as round from 25 upwards (avoid 2.5, 5, 7.5 ticks on small counts).
  const safe = step < 10 && !Number.isInteger(step) ? Math.ceil(step) : step;
  return safe * 4;
}

const shortDate = (iso: string) =>
  new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${iso}T00:00:00Z`),
  );

/** Multi-series line chart over days with a crosshair tooltip. One y-axis, shared scale. */
export function LineChart({
  dates,
  series,
  height = 240,
}: {
  dates: string[];
  series: LineSeries[];
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const titleId = useId();
  const W = 720;
  const pad = { top: 12, right: 12, bottom: 28, left: 36 };
  const iw = W - pad.left - pad.right;
  const ih = height - pad.top - pad.bottom;
  const max = niceMax(Math.max(1, ...series.flatMap((s) => s.values)));
  const n = dates.length;
  const x = (i: number) => pad.left + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
  const y = (v: number) => pad.top + ih - (v / max) * ih;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const labelEvery = Math.max(1, Math.ceil(n / 8));

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * iw;
    setHover(Math.max(0, Math.min(n - 1, Math.round((px / iw) * (n - 1)))));
  };

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${height}`} className="h-auto w-full" role="img" aria-labelledby={titleId}>
        <title id={titleId}>{series.map((s) => s.label).join(' and ')} per day</title>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth={1} />
            <text
              x={pad.left - 8}
              y={y(t)}
              dy="0.32em"
              textAnchor="end"
              className="fill-slate-400 text-[11px]"
            >
              {t}
            </text>
          </g>
        ))}
        {dates.map((d, i) =>
          // Regular labels, plus the last day — unless a regular label already sits too close to it.
          (i % labelEvery === 0 && n - 1 - i >= labelEvery / 2) || i === n - 1 ? (
            <text
              key={d}
              x={x(i)}
              y={height - 8}
              // Edge labels hug the plot so they're never clipped by the chart's border.
              textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
              className="fill-slate-400 text-[11px]"
            >
              {shortDate(d)}
            </text>
          ) : null,
        )}
        {series.map((s, si) => (
          <g key={s.label}>
            <path
              d={`M${s.values.map((v, i) => `${x(i)},${y(v)}`).join('L')}L${x(n - 1)},${y(0)}L${x(0)},${y(0)}Z`}
              fill={SERIES[si]}
              opacity={0.08}
            />
            <polyline
              points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
              fill="none"
              stroke={SERIES[si]}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </g>
        ))}
        {hover !== null && (
          <g pointerEvents="none">
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={pad.top}
              y2={pad.top + ih}
              stroke="#94a3b8"
              strokeWidth={1}
            />
            {series.map((s, si) => (
              <circle
                key={s.label}
                cx={x(hover)}
                cy={y(s.values[hover] ?? 0)}
                r={4.5}
                fill={SERIES[si]}
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </g>
        )}
        <rect
          x={pad.left}
          y={pad.top}
          width={iw}
          height={ih}
          fill="transparent"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {hover !== null && (
        <div
          className="shadow-pop pointer-events-none absolute top-2 z-10 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs"
          style={{
            left: `${(x(hover) / W) * 100}%`,
            transform: x(hover) > W * 0.65 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
          }}
        >
          <p className="mb-1 font-semibold text-slate-900">{shortDate(dates[hover]!)}</p>
          {series.map((s, si) => (
            <p key={s.label} className="flex items-center gap-2 text-slate-600">
              <span className="h-2 w-2 rounded-full" style={{ background: SERIES[si] }} aria-hidden />
              {s.label}
              <span className="ml-auto pl-3 font-semibold text-slate-900 tabular-nums">
                {s.values[hover]}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function Legend({ labels }: { labels: string[] }) {
  return (
    <ul className="flex flex-wrap gap-4 text-xs text-slate-600">
      {labels.map((l, i) => (
        <li key={l} className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 rounded" style={{ background: SERIES[i] }} aria-hidden />
          {l}
        </li>
      ))}
    </ul>
  );
}

/** Horizontal bars, one hue, value labelled at the bar end. */
export function BarList({ rows, total }: { rows: { label: string; value: number }[]; total?: number }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const sum = total ?? rows.reduce((s, r) => s + r.value, 0);
  if (rows.length === 0)
    return <p className="py-6 text-center text-sm text-slate-400">No data for this period</p>;
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => {
        const share = sum ? Math.round((r.value / sum) * 100) : 0;
        return (
          <li key={r.label} title={`${r.label}: ${formatNumber(r.value)} (${share}%)`}>
            <div className="mb-1 flex justify-between gap-3 text-xs">
              <span className="truncate text-slate-700">{r.label}</span>
              <span className="shrink-0 text-slate-500 tabular-nums">
                <span className="font-semibold text-slate-900">{formatNumber(r.value)}</span> · {share}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-2 rounded-full"
                style={{ width: `${Math.max(2, (r.value / max) * 100)}%`, background: SERIES[0] }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Tiny trend line for a KPI card — decorative; the number beside it carries the value. */
export function Sparkline({ values, color = SERIES[0] }: { values: number[]; color?: string }) {
  if (values.length < 2) return null;
  const W = 96;
  const H = 32;
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => [(i / (values.length - 1)) * W, H - 2 - (v / max) * (H - 4)] as const);
  const line = pts.map(([x, y]) => `${x},${y}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-8 w-24" aria-hidden>
      <polygon points={`0,${H} ${line} ${W},${H}`} fill={color} opacity={0.1} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Single-series column chart over a few labelled periods (e.g. months), with a per-bar tooltip.
 * Bars grow from a zero baseline with 4px rounded tops and a 2px gap between neighbours.
 */
export function ColumnChart({
  bars,
  formatValue = formatNumber,
  title,
  height = 220,
}: {
  bars: { label: string; value: number; detail?: string }[];
  formatValue?: (v: number) => string;
  title: string;
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const titleId = useId();
  const W = 720;
  const pad = { top: 16, right: 12, bottom: 28, left: 64 };
  const iw = W - pad.left - pad.right;
  const ih = height - pad.top - pad.bottom;
  const max = niceMax(Math.max(1, ...bars.map((b) => b.value)));
  const slot = iw / Math.max(1, bars.length);
  const bw = Math.min(64, slot * 0.6);
  const y = (v: number) => pad.top + ih - (v / max) * ih;
  const ticks = [0, 0.5, 1].map((f) => max * f);
  const r = 4;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${height}`} className="h-auto w-full" role="img" aria-labelledby={titleId}>
        <title id={titleId}>{title}</title>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth={1} />
            <text
              x={pad.left - 8}
              y={y(t)}
              dy="0.32em"
              textAnchor="end"
              className="fill-slate-400 text-[11px]"
            >
              {formatValue(t)}
            </text>
          </g>
        ))}
        {bars.map((b, i) => {
          const x = pad.left + i * slot + (slot - bw) / 2;
          const top = y(b.value);
          const h = pad.top + ih - top;
          const rr = Math.min(r, h / 2, bw / 2);
          return (
            <g key={b.label}>
              {h > 0 && (
                <path
                  d={`M${x},${pad.top + ih}V${top + rr}Q${x},${top} ${x + rr},${top}H${x + bw - rr}Q${x + bw},${top} ${x + bw},${top + rr}V${pad.top + ih}Z`}
                  fill={SERIES[0]}
                  opacity={hover === null || hover === i ? 1 : 0.55}
                />
              )}
              <text x={x + bw / 2} y={height - 8} textAnchor="middle" className="fill-slate-400 text-[11px]">
                {b.label}
              </text>
              {/* Hit target: the whole column slot, larger than the bar. */}
              <rect
                x={pad.left + i * slot}
                y={pad.top}
                width={slot}
                height={ih}
                fill="transparent"
                onPointerEnter={() => setHover(i)}
                onPointerLeave={() => setHover(null)}
              />
            </g>
          );
        })}
      </svg>
      {hover !== null && bars[hover] && (
        <div
          className="shadow-pop pointer-events-none absolute top-2 z-10 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs"
          style={{
            left: `${((pad.left + (hover + 0.5) * slot) / W) * 100}%`,
            transform: hover >= bars.length / 2 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
          }}
        >
          <p className="font-semibold text-slate-900">{bars[hover].label}</p>
          <p className="text-slate-600 tabular-nums">{formatValue(bars[hover].value)}</p>
          {bars[hover].detail && <p className="text-slate-500">{bars[hover].detail}</p>}
        </div>
      )}
    </div>
  );
}
